import fs from "node:fs";
import path from "node:path";

/**
 * Get SSL certificate paths from environment variables
 *
 * Environment variables:
 * - SSL_KEY_PATH: Path to private key file
 * - SSL_CERT_PATH: Path to certificate file (CRT)
 * - SSL_CA_PATH: Path to CA bundle/chain file
 *
 * Default paths (if env vars not set):
 * - ssl/.key (private key)
 * - ssl/.crt (certificate)
 * - ssl/ca.crt (CA bundle)
 */
export function getSSLCertificatePaths(): {
  keyPath: string;
  certPath: string;
  caPath: string;
} {
  const sslPath = process.env.SSL_PATH ?? path.join(process.cwd(), "ssl");

  // SSL_KEY_PATH: Private key path
  const keyPath = process.env.SSL_KEY_PATH ?? path.join(sslPath, ".key");

  // SSL_CERT_PATH: Certificate (CRT) path
  const certPath = process.env.SSL_CERT_PATH ?? path.join(sslPath, ".crt");

  // SSL_CA_PATH: CA bundle/chain path
  const caPath = process.env.SSL_CA_PATH ?? path.join(sslPath, "ca.crt");

  return { keyPath, certPath, caPath };
}

/**
 * Load SSL certificates from file paths
 */
export function loadSSLCertificates(): {
  key: string;
  cert: string;
  ca: string;
} {
  const { keyPath, certPath, caPath } = getSSLCertificatePaths();

  // Verify certificates exist
  if (!fs.existsSync(keyPath)) {
    throw new Error(`SSL private key not found: ${keyPath}`);
  }
  if (!fs.existsSync(certPath)) {
    throw new Error(`SSL certificate not found: ${certPath}`);
  }
  if (!fs.existsSync(caPath)) {
    throw new Error(`SSL CA certificate not found: ${caPath}`);
  }

  return {
    key: fs.readFileSync(keyPath, "utf8"),
    cert: fs.readFileSync(certPath, "utf8"),
    ca: fs.readFileSync(caPath, "utf8"),
  };
}
